import { Link, useLocation } from 'react-router-dom';
import { useDealerAuth } from '../context/DealerAuthContext';
import { useTheme } from '@/context/ThemeContext';

const navItems = [
  { icon: 'ri-dashboard-line', label: 'Overview', href: '/dealer/dashboard' },
  { icon: 'ri-file-list-3-line', label: 'My Orders', href: '/dealer/orders' },
  { icon: 'ri-add-circle-line', label: 'New Order', href: '/dealer/new-order' },
  { icon: 'ri-battery-2-charge-line', label: 'Products', href: '/dealer/products' },
  { icon: 'ri-user-line', label: 'My Account', href: '/dealer/account' },
];

interface Props {
  collapsed: boolean;
  onToggle: () => void;
}

export default function DealerSidebar({ collapsed, onToggle }: Props) {
  const location = useLocation();
  const { dealer, logout } = useDealerAuth();
  const { theme } = useTheme();
  const dark = theme === 'dark';

  const bg = dark ? 'bg-[#0D1B2A] border-r border-white/10' : 'bg-white border-r border-gray-200';
  const borderB = dark ? 'border-white/10' : 'border-gray-200';
  const logoText = dark ? 'invert' : '';
  const badgeBg = dark ? 'border-white/10' : 'border-gray-200';
  const badgeName = dark ? 'text-white' : 'text-gray-900';
  const badgeSub = dark ? 'text-white/40' : 'text-gray-400';
  const inactiveLink = dark ? 'text-white/60 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100';
  const logoutCls = dark ? 'text-white/40 hover:text-red-400 hover:bg-red-500/10' : 'text-gray-400 hover:text-red-500 hover:bg-red-50';
  const collapseCls = dark ? 'text-white/30 hover:text-white hover:bg-white/5' : 'text-gray-300 hover:text-gray-700 hover:bg-gray-100';

  return (
    <aside
      className={`flex flex-col h-screen sticky top-0 transition-all duration-300 ${collapsed ? 'w-16' : 'w-60'} ${bg}`}
    >
      {/* Logo */}
      <div className={`flex items-center gap-3 px-4 h-16 border-b ${borderB} shrink-0`}>
        {collapsed ? (
          <div className="w-8 h-8 flex items-center justify-center shrink-0">
            <i className="ri-anchor-line text-xl" style={{ color: 'var(--accent)' }}></i>
          </div>
        ) : (
          <div className="flex flex-col gap-0.5">
            <img
              src="https://static.readdy.ai/image/3bab8804b943b0f9d2e99ce4e4e8123d/a7b2a29c0c21ce8564d3744200077816.png"
              alt="Stingray"
              className={`h-7 w-auto object-contain brightness-0 ${logoText}`}
            />
            <span
              className="font-bold tracking-[0.22em] uppercase pl-0.5"
              style={{ fontSize: '9px', color: 'var(--accent)' }}
            >
              DEALER PORTAL
            </span>
          </div>
        )}
      </div>

      {/* Dealer badge */}
      {!collapsed && dealer && (
        <div className={`mx-3 mt-4 border rounded-lg px-3 py-2.5 ${badgeBg}`}>
          <p className={`text-xs font-bold truncate ${badgeName}`}>{dealer.name}</p>
          <p className={`text-[10px] mt-0.5 ${badgeSub}`}>{dealer.accountNumber}</p>
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {navItems.map((item) => {
          const active = location.pathname === item.href;
          return (
            <Link
              key={item.href}
              to={item.href}
              title={collapsed ? item.label : undefined}
              className={`flex items-center gap-3 px-4 py-2.5 mx-2 rounded-lg mb-1 transition-all cursor-pointer whitespace-nowrap ${
                active ? 'text-white' : inactiveLink
              }`}
              style={active ? { backgroundColor: 'var(--accent)' } : {}}
            >
              <div className="w-5 h-5 flex items-center justify-center shrink-0">
                <i className={`${item.icon} text-base`}></i>
              </div>
              {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* Bottom: logout + collapse */}
      <div className={`border-t p-3 space-y-1 shrink-0 ${borderB}`}>
        <button
          onClick={() => void logout()}
          title={collapsed ? 'Sign Out' : undefined}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${logoutCls}`}
        >
          <div className="w-5 h-5 flex items-center justify-center shrink-0">
            <i className="ri-logout-box-line text-base"></i>
          </div>
          {!collapsed && <span className="text-sm font-medium">Sign Out</span>}
        </button>
        <button
          onClick={onToggle}
          className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${collapseCls}`}
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className={`${collapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-left-s-line'} text-lg`}></i>
          </div>
        </button>
      </div>
    </aside>
  );
}
